// v2
// Path: frontend/src/components/QuickBooksSync/SearchableSelect.jsx
// Inputs: options: Array<{ value, label, description, sublabel, isLive, isPrimaryKey, required, isCustom, isMapped, type }>, value, onChange, placeholder, disabled, allowCustom
// Outputs: Rich searchable select dropdown component with keyboard navigation, live metadata pills, and custom value entry
// Summary: High-performance searchable dropdown for selecting source columns, tables, and target entities with instant filtering and custom field entry.

import React, { useState, useRef, useEffect } from 'react';

export default function SearchableSelect({
    options = [],
    value = '',
    onChange,
    placeholder = 'Search or select...',
    disabled = false,
    allowCustom = false,
    className = '',
    style = {}
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightIndex, setHighlightIndex] = useState(0);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    // Find current selected item
    const selectedOption = options.find(o => o.value === value);

    // Filter options matching search query (matches label, value, description, or sublabel)
    const filteredOptions = options.filter(opt => {
        if (!searchTerm.trim()) return true;
        const q = searchTerm.toLowerCase();
        return (
            (opt.label && opt.label.toLowerCase().includes(q)) ||
            (opt.value && opt.value.toLowerCase().includes(q)) ||
            (opt.description && opt.description.toLowerCase().includes(q)) ||
            (opt.sublabel && opt.sublabel.toLowerCase().includes(q))
        );
    });

    const isExactMatch = options.some(o => (o.value || '').toLowerCase() === searchTerm.trim().toLowerCase());
    const showCustomOption = allowCustom && searchTerm.trim().length > 0 && !isExactMatch;

    // Handle clicks outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
            setHighlightIndex(0);
        }
    }, [isOpen]);

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightIndex(prev => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filteredOptions[highlightIndex]) {
                handleSelect(filteredOptions[highlightIndex].value);
            } else if (showCustomOption) {
                handleSelect(searchTerm.trim());
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setIsOpen(false);
            setSearchTerm('');
        }
    };

    const handleSelect = (val) => {
        if (onChange) onChange(val);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div ref={containerRef} className={`relative inline-block ${className}`} style={{ minWidth: 220, ...style }}>
            {/* Trigger Button */}
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between text-left px-3 py-1.5 text-xs font-mono rounded bg-slate-900 border border-slate-700 text-slate-200 hover:border-sky-500 focus:outline-none focus:border-sky-400 disabled:opacity-50 transition-colors"
            >
                <span className="truncate pr-2">
                    {selectedOption ? (
                        <span className="flex items-center gap-1.5">
                            <span className="font-semibold text-sky-400">{selectedOption.value}</span>
                            {selectedOption.description && (
                                <span className="text-slate-400 font-sans text-[11px] truncate">({selectedOption.description})</span>
                            )}
                        </span>
                    ) : value ? (
                        <span className="font-semibold text-amber-300 font-mono">{value}</span>
                    ) : (
                        <span className="text-slate-500 font-sans">{placeholder}</span>
                    )}
                </span>
                <span className="text-slate-500 text-[10px] ml-1">▼</span>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div
                    className="absolute z-50 left-0 mt-1 w-full min-w-[300px] max-w-lg bg-slate-900 border border-slate-700 rounded-lg shadow-2xl overflow-hidden flex flex-col"
                    style={{ maxHeight: 320 }}
                >
                    {/* Search Input */}
                    <div className="p-2 border-b border-slate-800 bg-slate-950/80 flex items-center gap-2">
                        <span className="text-slate-400 text-xs">🔍</span>
                        <input
                            ref={inputRef}
                            type="text"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setHighlightIndex(0);
                            }}
                            onKeyDown={handleKeyDown}
                            placeholder={allowCustom ? "Search field or type custom..." : "Type to filter..."}
                            className="w-full bg-transparent border-none text-xs text-white placeholder-slate-500 focus:outline-none font-mono"
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() => setSearchTerm('')}
                                className="text-slate-400 hover:text-white text-xs px-1"
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    {/* Options List */}
                    <div ref={listRef} className="overflow-y-auto flex-1 divide-y divide-slate-800/40">
                        {filteredOptions.length === 0 && !showCustomOption ? (
                            <div className="px-3 py-3 text-center text-slate-500 text-xs font-sans">
                                No matching options
                            </div>
                        ) : (
                            filteredOptions.map((opt, idx) => {
                                const isSelected = opt.value === value;
                                const isHighlighted = idx === highlightIndex;
                                return (
                                    <div
                                        key={opt.value + '_' + idx}
                                        onClick={() => handleSelect(opt.value)}
                                        onMouseEnter={() => setHighlightIndex(idx)}
                                        className={`px-3 py-2 cursor-pointer transition-colors flex items-start justify-between gap-2 text-xs ${
                                            isSelected
                                                ? 'bg-sky-950/60 text-sky-200'
                                                : isHighlighted
                                                ? 'bg-slate-800/80 text-slate-100'
                                                : 'text-slate-300 hover:bg-slate-800/40'
                                        }`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`font-mono font-bold ${opt.isCustom ? 'text-purple-300' : 'text-sky-400'}`}>
                                                    {opt.value}
                                                </span>
                                                {opt.required && (
                                                    <span className="bg-rose-500/20 text-rose-400 text-[9px] px-1 py-0.2 rounded border border-rose-500/30 font-bold">
                                                        REQUIRED
                                                    </span>
                                                )}
                                                {opt.isCustom && (
                                                    <span className="bg-purple-500/20 text-purple-300 text-[9px] px-1 py-0.2 rounded border border-purple-500/30">
                                                        CUSTOM
                                                    </span>
                                                )}
                                                {opt.isMapped && (
                                                    <span className="bg-slate-700/60 text-slate-400 text-[9px] px-1 py-0.2 rounded">
                                                        MAPPED
                                                    </span>
                                                )}
                                                {opt.type && (
                                                    <span className="bg-slate-800 text-slate-400 text-[9px] px-1 py-0.2 rounded font-mono">
                                                        {opt.type}
                                                    </span>
                                                )}
                                                {opt.isPrimaryKey && (
                                                    <span className="bg-amber-500/20 text-amber-300 text-[9px] px-1 py-0.2 rounded border border-amber-500/30">
                                                        PK
                                                    </span>
                                                )}
                                                {opt.sqlType && (
                                                    <span className="bg-slate-800 text-slate-400 text-[9px] px-1 py-0.2 rounded">
                                                        {opt.sqlType}{opt.maxLength ? `(${opt.maxLength})` : ''}
                                                    </span>
                                                )}
                                                {opt.isLiveColumn && (
                                                    <span className="bg-emerald-500/20 text-emerald-400 text-[9px] px-1 py-0.2 rounded border border-emerald-500/30">
                                                        LIVE
                                                    </span>
                                                )}
                                            </div>
                                            {opt.description && (
                                                <p className="text-slate-400 text-[11px] truncate font-sans mt-0.5">
                                                    {opt.description}
                                                </p>
                                            )}
                                            {opt.cobolName && opt.cobolName !== opt.value && (
                                                <span className="text-slate-500 text-[10px] font-mono">
                                                    COBOL: {opt.cobolName}
                                                </span>
                                            )}
                                        </div>
                                        {isSelected && (
                                            <span className="text-sky-400 text-xs font-bold shrink-0">✓</span>
                                        )}
                                    </div>
                                );
                            })
                        )}

                        {/* Custom Option Tile */}
                        {showCustomOption && (
                            <div
                                onClick={() => handleSelect(searchTerm.trim())}
                                className="px-3 py-2 cursor-pointer bg-amber-950/40 hover:bg-amber-900/50 text-amber-300 text-xs border-t border-slate-700/80 flex items-center gap-2 font-mono transition-colors"
                            >
                                <span className="material-symbols-outlined text-[16px] text-amber-400">add_circle</span>
                                <div className="flex-1 truncate">
                                    Use custom field: <strong className="text-amber-200 underline">"{searchTerm.trim()}"</strong>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
